import type { Dispatch, SetStateAction } from 'react'
import type { LiveAiChatMessage } from './ai-chat-live-content'
import type { AiChatStreamEvent } from './ai-chat-stream-client'
import { AiChatMessageRole } from '@/graphql/schema'

type SentMessagesBySessionId = Record<string, Array<LiveAiChatMessage>>

function createStreamingAssistantMessage({
  content,
  sessionId,
  toolCalls,
}: {
  content: string
  sessionId: string
  toolCalls: LiveAiChatMessage['toolCalls']
}): LiveAiChatMessage {
  return {
    content,
    createdAt: new Date().toISOString(),
    id: `streaming-assistant-${sessionId}`,
    role: AiChatMessageRole.Assistant,
    sessionId,
    toolCalls,
    usage: null,
  }
}

function upsertStreamingAssistantMessage({
  content,
  currentMessages,
  sessionId,
  toolCalls,
}: {
  content: string
  currentMessages: Array<LiveAiChatMessage>
  sessionId: string
  toolCalls: LiveAiChatMessage['toolCalls']
}) {
  const assistantMessage = createStreamingAssistantMessage({
    content,
    sessionId,
    toolCalls,
  })
  const streamingMessageIndex = currentMessages.findIndex(
    (message) => message.id === assistantMessage.id,
  )

  if (streamingMessageIndex === -1) {
    return [...currentMessages, assistantMessage]
  }

  return currentMessages.map((message, index) =>
    index === streamingMessageIndex ? assistantMessage : message,
  )
}

function replaceStreamingAssistantMessage({
  assistantMessage,
  currentMessages,
  sessionId,
}: {
  assistantMessage: LiveAiChatMessage
  currentMessages: Array<LiveAiChatMessage>
  sessionId: string
}) {
  const streamingMessageId = `streaming-assistant-${sessionId}`

  if (!currentMessages.some((message) => message.id === streamingMessageId)) {
    return [...currentMessages, assistantMessage]
  }

  return currentMessages.map((message) =>
    message.id === streamingMessageId ? assistantMessage : message,
  )
}

export function applyAiChatStreamEvent({
  event,
  sessionId,
  setSentMessagesBySessionId,
  streamingContentRef,
  streamingToolCallsRef,
}: {
  event: AiChatStreamEvent
  sessionId: string
  setSentMessagesBySessionId: Dispatch<SetStateAction<SentMessagesBySessionId>>
  streamingContentRef: { current: string }
  streamingToolCallsRef: { current: LiveAiChatMessage['toolCalls'] }
}) {
  switch (event.type) {
    case 'user_message':
      setSentMessagesBySessionId((currentMessages) => ({
        ...currentMessages,
        [sessionId]: [...(currentMessages[sessionId] ?? []), event.userMessage],
      }))
      break
    case 'tool_call':
      streamingToolCallsRef.current = [
        ...streamingToolCallsRef.current,
        event.toolCall,
      ]
      setSentMessagesBySessionId((currentMessages) => ({
        ...currentMessages,
        [sessionId]: upsertStreamingAssistantMessage({
          content: streamingContentRef.current,
          currentMessages: currentMessages[sessionId] ?? [],
          sessionId,
          toolCalls: streamingToolCallsRef.current,
        }),
      }))
      break
    case 'assistant_delta':
      streamingContentRef.current += event.delta
      setSentMessagesBySessionId((currentMessages) => ({
        ...currentMessages,
        [sessionId]: upsertStreamingAssistantMessage({
          content: streamingContentRef.current,
          currentMessages: currentMessages[sessionId] ?? [],
          sessionId,
          toolCalls: streamingToolCallsRef.current,
        }),
      }))
      break
    case 'assistant_done':
      setSentMessagesBySessionId((currentMessages) => ({
        ...currentMessages,
        [sessionId]: replaceStreamingAssistantMessage({
          assistantMessage: event.assistantMessage,
          currentMessages: currentMessages[sessionId] ?? [],
          sessionId,
        }),
      }))
      break
    case 'input_request_updated':
      setSentMessagesBySessionId((currentMessages) => ({
        ...currentMessages,
        [sessionId]: (currentMessages[sessionId] ?? []).map((message) =>
          message.id === event.inputRequest.assistantMessageId
            ? { ...message, inputRequest: event.inputRequest }
            : message,
        ),
      }))
      break
    case 'session_updated':
      break
    case 'error':
      throw new Error(event.message)
  }
}
